/**
 * How long after a hunt ends an archive with no fixes reads as "still being
 * generated" rather than as "this is all there is".
 *
 * Two hours, matching mobile's `replayGenerationWindow` deliberately rather
 * than by coincidence: the two clients read the same archive from the same
 * service, and a hunt that mobile says is still building must not be described
 * by the web as having recorded nothing. Observed generation takes around half
 * an hour, so this is generous headroom.
 *
 * Past it, a hunt with no fixes almost certainly never had any — and telling
 * someone to come back later for a replay that will never arrive is worse than
 * showing them the empty one.
 */
export const REPLAY_GENERATION_WINDOW_MS = 2 * 60 * 60 * 1000

export interface ReplayReadinessInput {
  /** `bundle.endsAt`, epoch milliseconds. */
  endsAt: number
  /** How many participants the archive named. */
  participantCount: number
  /** Whether any participant carried at least one fix. */
  hasAnyFix: boolean
  /** Injectable for tests; defaults to now. */
  now?: number
}

/**
 * Whether an archive that parsed but carried no positions is still being
 * built, rather than being a hunt where nothing was recorded.
 *
 * The distinction is the whole point: a freshly ended hunt whose archive has
 * not finished generating was telling viewers "No positions were recorded
 * during this hunt", which is not merely unhelpful but false — the positions
 * exist and arrive minutes later.
 *
 * Mirrors mobile's `_isStillGenerating`, rule for rule, because the two clients
 * read one archive from one service and must not disagree about what it means:
 *
 * - **Participants but no fixes** is the shape generation is mid-way through.
 *   An archive naming nobody at all is a different thing and stays "empty".
 * - **The window is symmetric** around the end date. A negative age means the
 *   hunt's end date is in the future, which can only be clock skew between the
 *   viewer and the tracker — and skew implies the hunt ended moments ago, which
 *   is the strongest evidence there is that generation is still running. The
 *   symmetric bound covers the skewed client while still rejecting a nonsense
 *   date years out.
 */
export function isReplayStillGenerating({
  endsAt,
  participantCount,
  hasAnyFix,
  now = Date.now(),
}: ReplayReadinessInput): boolean {
  if (hasAnyFix) return false
  if (participantCount === 0) return false
  if (!Number.isFinite(endsAt)) return false
  return Math.abs(now - endsAt) <= REPLAY_GENERATION_WINDOW_MS
}
